import { Box, LinearProgress, Typography } from "@mui/material";
import { User } from "../../utils";
import React, { useEffect, useState } from "react";
import { Company, Employee } from "../../types";
import {
  canAccessCollaboratorsByRole,
  canAccessDashboardByRole,
  canAccessIntegrationsByRole,
} from "../../utils/roleUtils";
import { useEmployeeTerminology } from "../../utils/terminology";
import { useFeatures } from "../../context/FeatureContext";
import { useWhitelabel } from "../../context/WhitelabelContext";
import { FEATURES } from "../../constants/features";
import Analytics from "../analytics/Analytics";
import AppFrame from "../apps/AppFrame";
import Audio from "../audio/Audio";
import Agents from "../agents/Agents";
import Chat from "../chat/Chat";
import ChatLog from "../chat/ChatLog";
import ContentManager from "../content/ContentManager";
import LandingPagesManager from "../landing-pages/LandingPagesManager";
import AppBuilder from "../app-builder/AppBuilder";
import BuilderAppRuntime from "../app-builder/BuilderAppRuntime";
import Images from "../images/Images";
import Inboxes from "../inbox/Inboxes";
import Integrations from "../integrations/Integrations";
import Documents from "../knowledge_base/Documents";
import Lists from "../lists/Lists";
import MonitoringPage from "../monitoring/MonitoringPage";
import MarketingCampaigns from "../marketing-campaigns/MarketingCampaigns";
import Sequences from "../sequences/Sequences";
import Collaborators from "../settings/Collaborators";
import Video from "../video/Video";
import Voices from "../voices/Voices";
import WorkflowsMenu from "../workflow/WorkflowsMenu";
import PromptStudio from "../content/PromptStudio";
import Employees from "./Employees";
import TutorialWrapper from "../../components/TutorialWrapper";
import { useTutorialPage } from "../../hooks/useTutorialPage";
import { TUTORIAL_PAGES, TutorialPageKey } from "../../context/TutorialContext";
import {
  dashboardTutorialSteps,
  chatTutorialSteps,
  knowledgeBaseTutorialSteps,
  workflowsTutorialSteps,
  listsTutorialSteps,
  sequencesTutorialSteps,
  inboxTutorialSteps,
  contentEngineTutorialSteps,
  agentsTutorialSteps,
  integrationsTutorialSteps,
  collaboratorsTutorialSteps,
  imagesTutorialSteps,
  audioTutorialSteps,
  videoTutorialSteps,
  voicesTutorialSteps,
  promptStudioTutorialSteps,
} from "../../tutorials/tutorialSteps";
import { Step } from "react-joyride";

// Map subScreenName (tab) to tutorial page key and steps
const tutorialMapping: Record<string, { pageKey: TutorialPageKey; steps: Step[] }> = {
  dashboard: { pageKey: TUTORIAL_PAGES.DASHBOARD, steps: dashboardTutorialSteps },
  chats: { pageKey: TUTORIAL_PAGES.CHAT, steps: chatTutorialSteps },
  "knowledge-base": { pageKey: TUTORIAL_PAGES.KNOWLEDGE_BASE, steps: knowledgeBaseTutorialSteps },
  workflows: { pageKey: TUTORIAL_PAGES.WORKFLOWS, steps: workflowsTutorialSteps },
  lists: { pageKey: TUTORIAL_PAGES.LISTS, steps: listsTutorialSteps },
  sequences: { pageKey: TUTORIAL_PAGES.SEQUENCES, steps: sequencesTutorialSteps },
  inbox: { pageKey: TUTORIAL_PAGES.INBOX, steps: inboxTutorialSteps },
  "content-engine": { pageKey: TUTORIAL_PAGES.CONTENT_ENGINE, steps: contentEngineTutorialSteps },
  agents: { pageKey: TUTORIAL_PAGES.AGENTS, steps: agentsTutorialSteps },
  integrations: { pageKey: TUTORIAL_PAGES.INTEGRATIONS, steps: integrationsTutorialSteps },
  collaborators: { pageKey: TUTORIAL_PAGES.COLLABORATORS, steps: collaboratorsTutorialSteps },
  images: { pageKey: TUTORIAL_PAGES.IMAGES, steps: imagesTutorialSteps },
  audio: { pageKey: TUTORIAL_PAGES.AUDIO, steps: audioTutorialSteps },
  video: { pageKey: TUTORIAL_PAGES.VIDEO, steps: videoTutorialSteps },
  voices: { pageKey: TUTORIAL_PAGES.VOICES, steps: voicesTutorialSteps },
  "prompt-studio": { pageKey: TUTORIAL_PAGES.PROMPT_STUDIO, steps: promptStudioTutorialSteps },
};

// Component to run tutorial for a specific tab
const CompanyTutorialRunner: React.FC<{ tab: string }> = ({ tab }) => {
  const config = tutorialMapping[tab];

  const { runTutorial, steps, handleJoyrideCallback } = useTutorialPage({
    pageKey: config?.pageKey || TUTORIAL_PAGES.DASHBOARD,
    steps: config?.steps || [],
  });

  if (!config || steps.length === 0) {
    return null;
  }

  return (
    <TutorialWrapper
      run={runTutorial}
      steps={steps}
      callback={handleJoyrideCallback}
    />
  );
};

export default ({
  mode,
  currentUser,
  id,
  token,
  getToken,
  eventListener,
  openDialog,
  subScreenName,
  instanceId,
  company,
  currentCompanyPlan,
  employees,
  refreshEmployees,
  isSuperUser,
  instanceConfig,
  usage,
  companyRole,
  menuApps,
}: {
  mode: string;
  currentUser: any | User;
  id: string;
  token: any | string;
  getToken: () => Promise<string>;
  eventListener: Function;
  openDialog: Function;
  subScreenName: string | undefined;
  instanceId: string | undefined;
  company: Company;
  currentCompanyPlan: string;
  employees: Array<Employee | any>;
  refreshEmployees: () => void;
  isSuperUser?: boolean;
  instanceConfig?: any;
  usage?: any;
  companyRole?: string | null;
  menuApps?: Array<{
    id: string;
    name: string;
    icon: string;
    url?: string;
    appId?: string;
    public?: boolean;
    enabled?: boolean;
  }>;
}) => {
  const { isFeatureEnabled, isFeatureHidden } = useFeatures();
  const { whitelabelSettings } = useWhitelabel();
  const { employees: employeesText } = useEmployeeTerminology();
  const [tab, setTab] = useState(subScreenName || "employees");

  // The active app comes from the unified menuApps list from /usage, which
  // contains both whitelabel-configured apps and builder apps the user added
  // to their menu (we fall back to whitelabelSettings while /usage loads).
  // Apps with an absolute URL render in an iframe; everything else is a
  // builder app rendered by the AppRuntime.
  const activeApp =
    tab === "app"
      ? (menuApps || []).find((a) => a.id === instanceId) ||
        (whitelabelSettings?.apps || []).find((a) => a.id === instanceId) ||
        null
      : null;
  const hasIframeUrl =
    !!activeApp?.url &&
    (activeApp.url.startsWith("http://") || activeApp.url.startsWith("https://"));

  // Use the role passed from Dashboard (from /usage endpoint)
  const userRole = companyRole || null;
  // Role is loaded when companyRole prop is provided (even if null means no role)
  const roleCheckLoading = companyRole === undefined;

  useEffect(() => {
    setTab(subScreenName || "employees");
  }, [subScreenName]);

  if (!company) {
    return <LinearProgress />;
  }

  function capitalizeWords(str: string) {
    return str
      .split(" ")
      .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ")
      .split("-")
      .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  }

  // Get display label for the current tab, using custom terminology where applicable
  function getTabLabel(tabName: string) {
    if (tabName === "employees") {
      return employeesText;
    }
    if (tabName === "app") {
      return activeApp?.name || "App";
    }
    return capitalizeWords(tabName);
  }

  return (
    <Box sx={{ pt: tab === "chats" || tab === "app" ? 0 : 2, pl: tab === "chats" || tab === "app" ? 0 : { xs: 2, sm: 4 }, pr: tab === "chats" || tab === "app" ? 0 : { xs: 2, sm: 4 } }}>
      <CompanyTutorialRunner tab={tab} />
      <Typography
        variant="h4"
        style={{
          display: instanceId || tab === "chats" || tab === "app" ? "none" : "",
        }}
        sx={{ mb: 1 }}
      >
        {getTabLabel(tab)}
      </Typography>
      {tab === "app" && hasIframeUrl && (
        <AppFrame app={activeApp as any} />
      )}
      {tab === "app" && !hasIframeUrl && instanceId && (
        <BuilderAppRuntime
          appId={(activeApp as any)?.appId || instanceId}
          companyId={id}
          getToken={getToken as () => Promise<string>}
          isPublic={!!(activeApp as any)?.public}
        />
      )}
      {tab === "dashboard" && (canAccessDashboardByRole(userRole) || isFeatureEnabled(FEATURES.DASHBOARD)) && (
        <Analytics
          mode={mode}
          currentUser={currentUser}
          companyId={id || ""}
          token={token}
          getToken={getToken as () => Promise<string>}
          eventListener={eventListener as (event: any) => void}
          openDialog={openDialog as (dialog: any) => void}
          subScreenName={subScreenName}
          instanceId={instanceId}
        />
      )}
      {!roleCheckLoading &&
        !(canAccessDashboardByRole(userRole) || isFeatureEnabled(FEATURES.DASHBOARD)) &&
        tab === "dashboard" && (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Access Restricted
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Dashboard access is restricted. Please contact your administrator.
            </Typography>
          </Box>
        )}
      {tab === "employees" && isFeatureEnabled(FEATURES.EMPLOYEE) && (
        <Employees
          companyId={id}
          getToken={getToken as () => Promise<string>}
          openDialog={openDialog as (title: string, content: string, buttonText: string, onClick: () => void) => void}
          currentUser={currentUser}
          company={company}
          currentCompanyPlan={currentCompanyPlan}
          eventListener={eventListener as (event: any) => void}
          refreshEmployees={refreshEmployees}
          mode={mode}
          userRole={userRole}
          dashboardEmployees={employees}
        />
      )}
      {!roleCheckLoading && !isFeatureEnabled(FEATURES.EMPLOYEE) && tab === "employees" && (
        <Box sx={{ p: 3, textAlign: "center" }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Access Restricted
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {employeesText} access is restricted. Please contact your administrator.
          </Typography>
        </Box>
      )}
      {isFeatureEnabled(FEATURES.CHAT) && tab === "chats" && (
        <Chat
          currentUser={currentUser}
          companyId={id}
          id={instanceId}
          token={token}
          eventListener={eventListener}
          getToken={getToken}
          mode={mode}
          instanceConfig={instanceConfig}
          currentPlan={currentCompanyPlan}
          company={company}
          employees={employees}
          usage={usage}
          userRole={userRole}
        />
      )}
      {tab === "history" && (
        <ChatLog
          style={{ marginTop: 10, marginLeft: 10, marginRight: 10 }}
          companyId={id}
          getToken={getToken}
          currentUser={currentUser}
          perPage={30}
          currentChatId={null}
          eventListener={eventListener}
        />
      )}
      {tab === "knowledge-base" && isFeatureEnabled(FEATURES.KNOWLEDGE_BASE) && (
        <Documents
          currentUser={currentUser}
          companyId={id}
          token={token}
          getToken={getToken as () => Promise<string>}
          documentId={instanceId}
          eventListener={eventListener as (event: any) => void}
          openDialog={openDialog as (title: string, content: string, buttonText: string, onClick: () => void) => void}
          setTab={setTab}
          currentPlan={currentCompanyPlan}
          company={company}
          employees={employees}
          userRole={userRole}
        />
      )}
      {!roleCheckLoading && !isFeatureEnabled(FEATURES.KNOWLEDGE_BASE) && tab === "knowledge-base" && (
        <Box sx={{ p: 3, textAlign: "center" }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Access Restricted
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Knowledge Base access is restricted. Please contact your administrator.
          </Typography>
        </Box>
      )}
      {tab === "images" && isFeatureEnabled(FEATURES.IMAGE) && (
        <Images
          currentUser={currentUser}
          companyId={id}
          getToken={getToken}
          eventListener={eventListener}
        />
      )}
      {tab === "audio" && isFeatureEnabled(FEATURES.AUDIO) && (
        <Audio
          currentUser={currentUser}
          companyId={id}
          getToken={getToken}
          eventListener={eventListener}
        />
      )}
      {tab === "video" && isFeatureEnabled(FEATURES.VIDEO) && (
        <Video
          currentUser={currentUser}
          companyId={id}
          getToken={getToken}
          eventListener={eventListener}
        />
      )}
      {tab === "voices" && isFeatureEnabled(FEATURES.AUDIO) && (
        <Voices
          currentUser={currentUser}
          companyId={id}
          getToken={getToken}
          eventListener={eventListener}
        />
      )}
      {tab === "prompt-studio" && (
        <PromptStudio
          currentUser={currentUser}
          companyId={id}
          company={company}
          getToken={getToken}
          eventListener={eventListener}
          openDialog={openDialog}
          currentPlan={currentCompanyPlan}
        />
      )}
      {tab === "agents" && isFeatureEnabled(FEATURES.AGENT) && (
        <Agents
          currentUser={currentUser}
          companyId={id}
          company={company}
          getToken={getToken}
          eventListener={eventListener}
          currentPlan={currentCompanyPlan}
          employees={employees}
        />
      )}
      {tab === "integrations" &&
        !isFeatureHidden(FEATURES.INTEGRATIONS) &&
        isFeatureEnabled(FEATURES.INTEGRATIONS) &&
        canAccessIntegrationsByRole(userRole) && (
          <Integrations
            company={company}
            currentUser={currentUser}
            companyId={id}
            token={token}
            getToken={getToken}
            eventListener={eventListener}
            employees={employees}
            currentPlan={currentCompanyPlan}
          />
        )}
      {!roleCheckLoading &&
        !isFeatureHidden(FEATURES.INTEGRATIONS) &&
        (!isFeatureEnabled(FEATURES.INTEGRATIONS) ||
          !canAccessIntegrationsByRole(userRole)) &&
        tab === "integrations" && (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Access Restricted
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {!isFeatureEnabled(FEATURES.INTEGRATIONS)
                ? "Integrations access is not available on your current plan."
                : "Integrations access is restricted to company owners and administrators only."}
            </Typography>
          </Box>
        )}
      {tab === "collaborators" &&
        !isFeatureHidden(FEATURES.COLLABORATORS) &&
        isFeatureEnabled(FEATURES.COLLABORATORS) &&
        canAccessCollaboratorsByRole(userRole) && (
          <Collaborators
            openDialog={openDialog as (title: string, content: string, buttonText: string, onClick: () => void) => void}
            company={company}
            companyId={id}
            getToken={getToken as () => Promise<string>}
            currentUser={currentUser}
            eventListener={eventListener as (event: any) => void}
            userRole={userRole}
          />
        )}
      {!roleCheckLoading &&
        !isFeatureHidden(FEATURES.COLLABORATORS) &&
        (!isFeatureEnabled(FEATURES.COLLABORATORS) ||
          !canAccessCollaboratorsByRole(userRole)) &&
        tab === "collaborators" && (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Access Restricted
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {!isFeatureEnabled(FEATURES.COLLABORATORS)
                ? "Collaborators management is not available on your current plan."
                : "Collaborators management is restricted to company owners and administrators only."}
            </Typography>
          </Box>
        )}
      {tab === "workflows" && isFeatureEnabled(FEATURES.WORKFLOW) && (
        <WorkflowsMenu
          getToken={getToken}
          companyId={id}
          currentUser={currentUser}
        />
      )}
      {tab === "lists" && isFeatureEnabled(FEATURES.SMART_LIST) && (
        <Lists
          mode={mode}
          currentUser={currentUser}
          company={company}
          companyId={id}
          getToken={getToken as () => Promise<string>}
          eventListener={eventListener as (event: any) => void}
          currentPlan={currentCompanyPlan}
          instanceId={instanceId}
          openDialog={openDialog as (title: string, content: string, buttonText: string, onClick: () => void) => void}
        />
      )}
      {tab === "sequences" && isFeatureEnabled(FEATURES.SEQUENCE) && (
        <Sequences
          currentUser={currentUser}
          company={company}
          companyId={id}
          getToken={getToken as () => Promise<string>}
          eventListener={eventListener as (event: any) => void}
          openDialog={openDialog as (title: string, content: string, buttonText: string, onClick: () => void) => void}
          employees={employees}
          isSuperUser={isSuperUser}
        />
      )}
      {tab === "inbox" && isFeatureEnabled(FEATURES.INBOX) && (
        <Inboxes
          currentUser={currentUser}
          company={company}
          companyId={id}
          getToken={getToken as () => Promise<string>}
          eventListener={eventListener as (event: any) => void}
          openDialog={openDialog as (title: string, content: string, buttonText: string, onClick: () => void) => void}
          employees={employees}
        />
      )}
      {tab === "content-engine" && isFeatureEnabled(FEATURES.CONTENT_ENGINE) && (
        <ContentManager
          currentUser={currentUser}
          company={company}
          companyId={id}
          getToken={getToken}
          eventListener={eventListener}
          openDialog={openDialog}
          currentPlan={currentCompanyPlan}
        />
      )}
      {isFeatureEnabled(FEATURES.LANDING_PAGES) && tab === "landing-pages" && (
        <LandingPagesManager
          currentUser={currentUser}
          company={company}
          companyId={id}
          employees={employees}
          getToken={getToken as () => Promise<string>}
          eventListener={eventListener as (event: any) => void}
          currentPlan={currentCompanyPlan}
        />
      )}
      {!roleCheckLoading && !isFeatureEnabled(FEATURES.LANDING_PAGES) && tab === "landing-pages" && (
        <Box sx={{ p: 3, textAlign: "center" }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Landing Pages Unavailable
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Landing Pages is not available on your current plan. Please upgrade to access this feature.
          </Typography>
        </Box>
      )}
      {isFeatureEnabled(FEATURES.KEYWORD_MONITORING) && tab === "monitoring" && (
        <MonitoringPage
          currentUser={currentUser}
          companyId={id}
          getToken={getToken as () => Promise<string>}
          eventListener={eventListener as (event: any) => void}
        />
      )}
      {!roleCheckLoading && !isFeatureEnabled(FEATURES.KEYWORD_MONITORING) && tab === "monitoring" && (
        <Box sx={{ p: 3, textAlign: "center" }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Keyword Monitoring Unavailable
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Keyword Monitoring is not available. Please contact your administrator.
          </Typography>
        </Box>
      )}
      {tab === "marketing-campaigns" && isFeatureEnabled(FEATURES.MARKETING_CAMPAIGN_EMAIL) && (
        <MarketingCampaigns
          currentUser={currentUser}
          companyId={id}
          getToken={getToken as () => Promise<string>}
          eventListener={eventListener as (event: any) => void}
          openDialog={openDialog as (title: string, content: string, buttonText: string, onClick: () => void) => void}
        />
      )}
      {tab === "app-builder" && (
        <AppBuilder
          getToken={getToken as () => Promise<string>}
          companyId={id as string}
          currentUser={currentUser}
          openDialog={openDialog as (title: string, content: string, buttonText: string, onClick: () => void) => void}
          initialMenuAppIds={(usage?.menuAppIds || []).map(String)}
        />
      )}
    </Box>
  );
};
